/**
 * Most Konštrukcia → Korpus Builder: prevod parametrického komponentu
 * knižnice (naučeného z TopSolid dát) na konfiguráciu buildera.
 *
 * Komponent nesie `builder` blok (typ konštrukcie, chrbát, param_map),
 * z ktorého sa poskladá konfig v tvare `buildKonfig()` — builder sa ním
 * predvyplní a user môže korpus ďalej upravovať s 2D/3D náhľadom.
 */

import { novyOtvor } from "./generator";
import { nulovePresahy } from "./types";
import type { ChrbatTyp, Otvor, TypCela, TypKonstrukcie } from "./types";
import type { KomponentMeta } from "@/api/schemas";

/** Default hodnota parametra komponentu podľa mena (z params defs). */
function defaultParam(meta: KomponentMeta, name: string | undefined): unknown {
  if (!name) return undefined;
  return meta.params.find((p) => p.name === name)?.default ?? undefined;
}

function cislo(v: unknown): number | null {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : null;
}

/**
 * Konfigurácia buildera z komponentu knižnice (tvar `buildKonfig()`).
 * Vracia null, ak komponent nemá `builder` blok — vtedy sa použije
 * priame vloženie cez formulár (dry-run + z-komponentu endpoint).
 */
export function konfigZKomponentu(
  meta: KomponentMeta,
): Record<string, unknown> | null {
  const b = meta.builder;
  if (!b) return null;
  const map = (b.param_map ?? {}) as Record<string, string>;
  const def = (pole: string): unknown => defaultParam(meta, map[pole]);

  const sirka = cislo(def("sirka"));
  const vyska = cislo(def("vyska"));
  const hlbka = cislo(def("hlbka"));
  if (sirka == null || vyska == null || hlbka == null) return null;
  const t = cislo(def("t")) ?? 18;

  const root: Otvor = novyOtvor();
  const police = cislo(def("police"));
  if (police != null && police > 0) root.police = Math.round(police);

  const kridla = cislo(def("dvere_kridla"));
  if (kridla != null && kridla >= 1) {
    const typCela = String(def("dvere_typ") ?? "nalozene") as TypCela;
    const zavesy = cislo(def("zavesy_na_kridlo"));
    root.dvierka = {
      kridla: kridla >= 2 ? 2 : 1,
      typ: typCela,
      zavesy: zavesy != null ? Math.round(zavesy) : null,
    };
  }

  return {
    verzia: 1,
    params: {
      sirka: String(sirka),
      vyska: String(vyska),
      hlbka: String(hlbka),
      t: String(t),
      typ: b.typ as TypKonstrukcie,
      chrbat: b.chrbat as ChrbatTyp,
      presahy: nulovePresahy(),
      // komponenty z TopSolid nemajú nohy — user si ich zapne v builderi
      nohy: { enabled: false, vyska: "100", pocet: "", material: null },
      zaves: null,
      vysuv: null,
      slot_kod: null,
    },
    root,
  };
}
